import { useState, useEffect } from 'react';
import { FileText, Download, ExternalLink, Calendar, DollarSign, Package, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { getInvoices, getCredits } from '../utils/api';
import { motion } from 'motion/react';

interface Invoice {
  id: string;
  invoiceNumber: string;
  purchaseId: string;
  amount: number;
  credits: number;
  package: string;
  date: string;
  invoiceUrl: string;
  invoicePdf: string;
  status: string;
  refunded?: boolean;
  refundedAt?: string;
}

const INVOICES_PER_PAGE = 5;

export function Billing() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [availableCredits, setAvailableCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both invoices and credits in parallel
      const [invoicesData, creditsData] = await Promise.all([
        getInvoices(),
        getCredits()
      ]);
      
      console.log('[BILLING] Loaded invoices:', invoicesData);
      console.log('[BILLING] Loaded credits:', creditsData);
      
      const refundedInvoices = invoicesData.filter((inv: Invoice) => inv.refunded);
      if (refundedInvoices.length > 0) {
        console.log('[BILLING] 🔴 Refunded invoices:', refundedInvoices);
      }
      
      setInvoices(invoicesData);
      setAvailableCredits(creditsData.balance || 0);
    } catch (err) {
      console.error('Failed to load billing data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPackageName = (packageId: string) => {
    return packageId.charAt(0).toUpperCase() + packageId.slice(1);
  };

  const totalPages = Math.ceil(invoices.length / INVOICES_PER_PAGE);
  const currentInvoices = invoices.slice((currentPage - 1) * INVOICES_PER_PAGE, currentPage * INVOICES_PER_PAGE);

  return (
    <>
      <SEO 
        title="Billing & Invoices - HelioROI"
        description="View your payment history and download invoices"
      />
      
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">Billing & Invoices</h1>
              <p className="text-slate-600">
                View your payment history and download invoices for your credit purchases
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && invoices.length === 0 && (
          <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Invoices Yet</h3>
            <p className="text-slate-600 mb-6">
              Your invoices will appear here after you purchase credits
            </p>
          </div>
        )}

        {/* Invoices List */}
        {!loading && !error && invoices.length > 0 && (
          <div className="space-y-4">
            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-emerald-700 mb-1">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Total Invoices</span>
                </div>
                <p className="text-2xl font-bold text-emerald-900">{invoices.length}</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-700 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm font-medium">Net Spent</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">
                  ${invoices.reduce((sum, inv) => sum + (inv.refunded ? 0 : inv.amount), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-purple-700 mb-1">
                  <Package className="w-4 h-4" />
                  <span className="text-sm font-medium">Available Credits</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {availableCredits.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Invoices Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Invoice
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Package
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Credits
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {currentInvoices.map((invoice, index) => (
                      <motion.tr
                        key={invoice.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`transition-colors ${
                          invoice.refunded 
                            ? 'bg-red-50 hover:bg-red-100' 
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <FileText className={`w-4 h-4 ${invoice.refunded ? 'text-red-400' : 'text-slate-400'}`} />
                            <span className={`text-sm font-medium ${invoice.refunded ? 'text-red-900' : 'text-slate-900'}`}>
                              {invoice.invoiceNumber || `INV-${invoice.id.slice(-8).toUpperCase()}`}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`flex items-center gap-2 text-sm ${invoice.refunded ? 'text-red-600' : 'text-slate-600'}`}>
                            <Calendar className={`w-4 h-4 ${invoice.refunded ? 'text-red-400' : 'text-slate-400'}`} />
                            {formatDate(invoice.date)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            invoice.refunded 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {getPackageName(invoice.package)}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${invoice.refunded ? 'text-red-900' : 'text-slate-900'}`}>
                          {invoice.refunded && '-'}{invoice.credits.toLocaleString()} credits
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${invoice.refunded ? 'text-red-900' : 'text-slate-900'}`}>
                          {invoice.refunded && '-'}${invoice.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            invoice.refunded 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {invoice.refunded ? 'Refunded' : invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={invoice.invoicePdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                                invoice.refunded
                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                              }`}
                              title="Download PDF"
                            >
                              <Download className="w-3.5 h-3.5" />
                              PDF
                            </a>
                            <a
                              href={invoice.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${
                                invoice.refunded
                                  ? 'bg-red-700 hover:bg-red-800 text-white'
                                  : 'bg-slate-600 hover:bg-slate-700 text-white'
                              }`}
                              title="View Online"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              View
                            </a>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-sm text-slate-500">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}